/* globals window, ResizeObserver */
import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Auto-growing textarea sizing for the Scout composer (BIR-277).
 *
 * Both composers in AssistantWidget were fixed-height: the empty state at
 * `rows={3}` and the follow-up at `rows={1}`, neither with any resize logic.
 * The reported symptom — "the input collapses to one row in long chats" — is
 * really the switch between those two elements, which happens the moment the
 * first message lands, not gradually with conversation length. Nothing here
 * depends on the message count, which is what makes the two behave alike.
 *
 * Measuring rather than hardcoding a pixel cap keeps one hook honest across
 * both composers: they have different line-heights (1.4 vs 1.45), padding
 * (10/12 vs 14/40/14) and border widths (1px vs 1.5px), so a shared magic
 * number would mean a different row count in each.
 */

/** jsdom and SSR have no layout; don't warn about useLayoutEffect there. */
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Fallbacks for when a computed style isn't a usable number. */
const FALLBACK_LINE_HEIGHT_RATIO = 1.4;
const FALLBACK_FONT_SIZE = 14;

const px = (value: string): number => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * One text line, in px. `line-height` computes to a px value in browsers, but
 * to the unitless ratio (or `normal`) in jsdom, so both are handled.
 */
export function resolveLineHeight(style: CSSStyleDeclaration): number {
  const raw = style.lineHeight;
  const fontSize = px(style.fontSize) || FALLBACK_FONT_SIZE;

  const direct = parseFloat(raw);
  if (Number.isFinite(direct)) {
    // A bare ratio ("1.4") vs a length ("21.28px"). Anything without a unit is
    // a multiplier of the font size.
    return /^[\d.]+$/.test(raw.trim()) ? direct * fontSize : direct;
  }
  return fontSize * FALLBACK_LINE_HEIGHT_RATIO;
}

export interface AutoGrowBounds {
  /** Resting height with no content, in rows. */
  minRows: number;
  /** Grows to this many rows, then scrolls internally. */
  maxRows: number;
}

/**
 * BIR-277 asks for ~6-8 rows before internal scrolling, and for the fresh
 * chat and a long one to behave the same. Both composers share this ceiling,
 * so "the same" is enforced here rather than by two matching literals that
 * can drift apart.
 */
export const COMPOSER_MAX_ROWS = 8;

/** Empty state: 3 rows at rest — its placeholder is two lines on its own. */
export const EMPTY_STATE_COMPOSER_BOUNDS: AutoGrowBounds = {
  minRows: 3,
  maxRows: COMPOSER_MAX_ROWS,
};

/** Follow-up: 1 row at rest, to leave the transcript as much room as it can. */
export const FOLLOW_UP_COMPOSER_BOUNDS: AutoGrowBounds = {
  minRows: 1,
  maxRows: COMPOSER_MAX_ROWS,
};

export interface AutoGrowMetrics {
  height: number;
  maxHeight: number;
  /** True once content exceeds maxRows, so the caller shows a scrollbar. */
  overflowing: boolean;
}

/**
 * The height a textarea should take for its current content.
 *
 * `scrollHeight` already includes padding but never the border, while these
 * textareas are `box-sizing: border-box` — so the border has to be added back
 * or every box sits ~2px short and shows a scrollbar it doesn't need.
 */
export function measureAutoGrow(
  el: HTMLTextAreaElement,
  { minRows, maxRows }: AutoGrowBounds,
  contentHeight: number,
): AutoGrowMetrics {
  const style = window.getComputedStyle(el);
  const lineHeight = resolveLineHeight(style);
  const padding = px(style.paddingTop) + px(style.paddingBottom);
  const border = px(style.borderTopWidth) + px(style.borderBottomWidth);
  const chrome = padding + border;

  const minHeight = minRows * lineHeight + chrome;
  const maxHeight = maxRows * lineHeight + chrome;
  // contentHeight is a scrollHeight: padding in, border out.
  const desired = contentHeight + border;

  return {
    height: Math.min(Math.max(desired, minHeight), maxHeight),
    maxHeight,
    overflowing: desired > maxHeight,
  };
}

/**
 * Sizes `el` to its content within `bounds`. Exported for the spec, which
 * drives it directly rather than trying to make jsdom lay text out.
 */
export function applyAutoGrow(
  el: HTMLTextAreaElement,
  bounds: AutoGrowBounds,
): AutoGrowMetrics {
  // Collapse first, or scrollHeight reports the height we set last time and
  // the box can only ever grow.
  el.style.height = 'auto';
  const metrics = measureAutoGrow(el, bounds, el.scrollHeight);
  el.style.height = `${metrics.height}px`;
  el.style.overflowY = metrics.overflowing ? 'auto' : 'hidden';
  return metrics;
}

/**
 * Returns a ref to attach to a textarea. Re-measures when `value` changes —
 * including back down to `minRows` when the composer is cleared on submit —
 * and when the element's own width changes, since the assistant panel is
 * drag-resizable and narrowing it rewraps the text into more lines.
 */
export function useAutoGrow(
  value: string,
  bounds: AutoGrowBounds,
): React.RefObject<HTMLTextAreaElement | null> {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const { minRows, maxRows } = bounds;

  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const resize = () => applyAutoGrow(el, { minRows, maxRows });
    resize();

    if (typeof ResizeObserver === 'undefined') return undefined;
    // Observing the textarea catches both a panel drag and the panel opening
    // at a persisted width.
    const observer = new ResizeObserver(resize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, minRows, maxRows]);

  return ref;
}
